import { useMemo, useState } from 'react';

import type { EmployeeNotesSeed } from '@/api/performance-profile';
import type {
    EmployeeNote,
    EmployeeNoteCategory,
    EmployeeNoteDraft,
    EmployeeNoteOriginFilter,
} from '@/types/employee-note';

import { isRefusedError } from '@/api/api';
import { useGetUsersByIds } from '@/api/directory';
import {
    useCreateEmployeeNote,
    useDeleteEmployeeNote,
    useGetEmployeeNotes,
    useUpdateEmployeeNote,
} from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

/** Names the authors' read in the query cache, whichever screen shows the notes. */
export const EMPLOYEE_NOTE_AUTHORS_IDENTIFIER = 'employee-note-list';

/** What the screen says when a write fails. */
export interface EmployeeNoteWriteErrors {
    createError: string;
    updateError: string;
    deleteError: string;
}

interface UseEmployeeNoteListParams {
    employeeId: string;
    /** One folder, or `null` for every folder. */
    category: EmployeeNoteCategory | null;
    pageSize: number;
    /** Narrows the list to the notes written in one meeting or assessment; new notes carry it too. */
    origin?: EmployeeNoteOriginFilter;
    /** A first page another query already fetched. */
    seed?: EmployeeNotesSeed;
    writeErrors: EmployeeNoteWriteErrors;
    /** Confirms each saved, updated or deleted note with a toast. */
    announceWrites?: boolean;
    /** Runs once a new note is saved, with what was written. */
    onCreated?: (draft: EmployeeNoteDraft) => void;
}

/**
 * One page of an employee's notes with their authors, and the means to add, edit and delete them:
 * the state every notes screen shares, so each one only decides how the notes look.
 */
export const useEmployeeNoteList = ({
    employeeId,
    category,
    pageSize,
    origin = {},
    seed,
    writeErrors,
    announceWrites = false,
    onCreated,
}: UseEmployeeNoteListParams) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    const [page, setPage] = useState(1);
    // `RichText` only reads its value on mount, so the composer is remounted rather than cleared:
    // once a note is saved, and once a draft is dismissed, the next one starts empty.
    const [draftKey, setDraftKey] = useState(0);
    const startNewDraft = () => setDraftKey((current) => current + 1);
    const [noteIdToDelete, setNoteIdToDelete] = useState<string | null>(null);
    // Only the card being saved shows its spinner, not every card on the page.
    const [savingNoteId, setSavingNoteId] = useState<string | null>(null);

    const { data, isLoading, isError, error } = useGetEmployeeNotes({
        employeeId,
        category,
        pageNumber: page,
        pageSize,
        seed,
        origin,
    });
    const notes = useMemo(() => data?.items ?? [], [data?.items]);
    const totalCount = data?.totalCount ?? 0;

    const authorIds = useMemo(
        () => [
            ...new Set(
                notes
                    .map((note) => note.authorId)
                    .filter((authorId): authorId is string => !!authorId),
            ),
        ],
        [notes],
    );
    const { data: authors = [] } = useGetUsersByIds({
        userIds: authorIds,
        identifier: EMPLOYEE_NOTE_AUTHORS_IDENTIFIER,
    });
    const authorsById = useMemo(
        () => new Map(authors.map((author) => [author.id, author])),
        [authors],
    );

    const createNote = useCreateEmployeeNote(employeeId);
    const updateNote = useUpdateEmployeeNote(employeeId);
    const deleteNote = useDeleteEmployeeNote(employeeId);

    const reportError = (caught: unknown, message: string) =>
        handleError({ error: caught, data: { title: t('errors.genericTitle'), message } });

    const announce = (message: string) => {
        if (announceWrites) {
            showSuccessToast({ message });
        }
    };

    /** Resolves `true` once saved. */
    const createNoteFromDraft = async (draft: EmployeeNoteDraft) => {
        try {
            await createNote.mutateAsync({
                ...draft,
                thematicMeetingId: origin.thematicMeetingId,
                cycleInstanceId: origin.cycleInstanceId,
            });
            announce(t('performanceProfile.employeeNotes.created'));
            startNewDraft();
            // The server sorts the newest first, so the note just written lives on page one.
            setPage(1);
            onCreated?.(draft);

            return true;
        } catch (caught) {
            reportError(caught, writeErrors.createError);

            return false;
        }
    };

    /** A full replacement: the note keeps its links, only its text and folder change. Resolves
     * `true` once saved, so the card closes its editor only then. */
    const updateNoteFromDraft = async (note: EmployeeNote, draft: EmployeeNoteDraft) => {
        setSavingNoteId(note.id);
        try {
            await updateNote.mutateAsync({ noteId: note.id, request: draft });
            announce(t('performanceProfile.employeeNotes.updated'));

            return true;
        } catch (caught) {
            reportError(caught, writeErrors.updateError);

            return false;
        } finally {
            setSavingNoteId(null);
        }
    };

    const confirmDelete = async () => {
        if (!noteIdToDelete) {
            return;
        }

        try {
            await deleteNote.mutateAsync(noteIdToDelete);
            announce(t('performanceProfile.employeeNotes.deleted'));
            // The last note of a later page is gone: step back rather than show an empty page.
            if (notes.length === 1 && page > 1) {
                setPage(page - 1);
            }
        } catch (caught) {
            reportError(caught, writeErrors.deleteError);
        } finally {
            setNoteIdToDelete(null);
        }
    };

    return {
        notes,
        authorsById,
        isLoading,
        isError,
        isForbidden: isError && isRefusedError(error),
        page,
        setPage,
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
        pageRange: {
            start: totalCount === 0 ? 0 : (page - 1) * pageSize + 1,
            end: Math.min(page * pageSize, totalCount),
            total: totalCount,
        },
        draftKey,
        startNewDraft,
        createNote: createNoteFromDraft,
        isCreating: createNote.isPending,
        updateNote: updateNoteFromDraft,
        isSavingNote: (noteId: string) => updateNote.isPending && savingNoteId === noteId,
        noteIdToDelete,
        setNoteIdToDelete,
        confirmDelete,
        isDeleting: deleteNote.isPending,
    };
};
